import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Modal } from "@/components/ui";

interface SshKeyResponse {
  pubkey: string | null;
  key_path: string;
}

export function SshKeyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const q = useQuery<SshKeyResponse>({
    queryKey: ["ssh-pubkey"],
    queryFn: () => api<SshKeyResponse>("/api/admin/ssh-pubkey"),
    enabled: open,
  });

  const pubkey = q.data?.pubkey ?? "";

  const script = pubkey
    ? `sudo bash -c '
mkdir -p /root/.ssh && chmod 700 /root/.ssh
echo "${pubkey}" >> /root/.ssh/authorized_keys
chmod 600 /root/.ssh/authorized_keys
sed -i "s/^#*PermitRootLogin.*/PermitRootLogin prohibit-password/" /etc/ssh/sshd_config
(systemctl restart ssh || systemctl restart sshd)
echo OK'`
    : "";

  return (
    <Modal open={open} onClose={onClose} title="SSH public key" maxWidth="max-w-2xl">
      {q.isLoading && <div className="text-zinc-400 text-sm">Загружаем ключ…</div>}
      {q.isError && <div className="text-red-400 text-sm">Ошибка загрузки ключа</div>}

      {q.data && !pubkey && (
        <div className="text-sm text-red-400">
          Ключ не найден по пути <code>{q.data.key_path}.pub</code> на сервере бота.
        </div>
      )}

      {pubkey && (
        <div className="space-y-5">
          <div>
            <div className="text-sm text-zinc-300 mb-2">
              Этот ключ нужно вставить в{" "}
              <code className="text-zinc-100">/root/.ssh/authorized_keys</code> на новом
              сервере — тогда бот сможет ходить туда по SSH для install / restart / logs.
            </div>
            <CopyBlock content={pubkey} monoClass="break-all" />
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <div className="text-sm uppercase tracking-wider text-zinc-500 mb-2">
              Инструкция для нового сервера
            </div>
            <div className="text-sm text-zinc-400 mb-3">
              Зайди на новый VPS любым способом (пароль, GCP SSH, другая учётка) и
              выполни одним блоком:
            </div>
            <CopyBlock content={script} monoClass="whitespace-pre" />
            <ul className="text-xs text-zinc-500 mt-3 space-y-1">
              <li>• Создаёт <code>/root/.ssh/authorized_keys</code></li>
              <li>• Добавляет наш ключ</li>
              <li>• Разрешает SSH root-вход только по ключу</li>
              <li>• Перезапускает sshd</li>
            </ul>
            <div className="text-sm text-zinc-400 mt-4">
              После этого возвращайся в админку, «+ Добавить сервер» → жми Install.
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function CopyBlock({ content, monoClass = "" }: { content: string; monoClass?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-3 items-stretch">
      <pre
        className={`flex-1 px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-800 font-mono text-xs text-zinc-100 overflow-auto max-h-64 ${monoClass}`}
      >
        {content}
      </pre>
      <Button
        variant="ghost"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* ignore */
          }
        }}
      >
        {copied ? "Скопировано ✓" : "Копировать"}
      </Button>
    </div>
  );
}
